import { useState, useEffect, useCallback } from 'react';
import { scenarioStorage } from '@/services/scenarioStorage';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { toast } from 'sonner';
import type { SavedScenarioSet, SaveScenarioParams, LoadScenarioResult, ScenarioSetListItem } from '@/types/supremeIntelligenceTypes';

export function useSavedScenarios() {
  const { user } = useAuth();
  const [savedScenarios, setSavedScenarios] = useState<ScenarioSetListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const userId = user?.id;

  // Load scenario list on mount and when user changes
  const refreshList = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const scenarios = await scenarioStorage.listScenarioSets(userId);
      setSavedScenarios(scenarios);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load scenarios');
      setError(error);
      console.error('Failed to refresh scenario list:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  // Sync localStorage to cloud on login
  useEffect(() => {
    if (userId) {
      scenarioStorage.syncLocalToCloud(userId).then(count => {
        if (count > 0) {
          toast.success(`${count} scenario${count > 1 ? 's' : ''} synced to your account`);
          refreshList();
        }
      });
    }
  }, [userId, refreshList]);

  const saveScenarios = useCallback(async (params: SaveScenarioParams): Promise<SavedScenarioSet> => {
    setError(null);
    try {
      const saved = await scenarioStorage.saveScenarioSet(params, userId);
      await refreshList();
      return saved;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to save scenarios');
      setError(error);
      throw error;
    }
  }, [userId, refreshList]);

  const loadScenarios = useCallback(async (id: string): Promise<LoadScenarioResult | null> => {
    setError(null);
    try {
      const result = await scenarioStorage.loadScenarioSet(id);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load scenarios');
      setError(error);
      throw error;
    }
  }, []);

  const deleteScenarios = useCallback(async (id: string): Promise<void> => {
    setError(null);
    try {
      const success = await scenarioStorage.deleteScenarioSet(id);
      if (!success) {
        throw new Error('Failed to delete scenario set');
      }
      await refreshList();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete scenarios');
      setError(error);
      throw error;
    }
  }, [refreshList]);

  const updateScenarios = useCallback(async (id: string, updates: Partial<SaveScenarioParams>): Promise<void> => {
    setError(null);
    try {
      await scenarioStorage.updateScenarioSet(id, updates);
      await refreshList();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update scenarios');
      setError(error);
      throw error;
    }
  }, [refreshList]);

  const duplicateScenarios = useCallback(async (id: string, newName: string): Promise<void> => {
    setError(null);
    try {
      await scenarioStorage.duplicateScenarioSet(id, newName);
      await refreshList();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to duplicate scenarios');
      setError(error);
      throw error;
    }
  }, [refreshList]);

  return {
    savedScenarios,
    isLoading,
    error,
    saveScenarios,
    loadScenarios,
    deleteScenarios,
    updateScenarios,
    duplicateScenarios,
    refreshList,
  };
}
